import { Button, HStack } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { Table } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import { FilterChips } from "@/ui/tables/FilterChips";
import {
  LuCheck,
  LuCopy,
  LuDownload,
  LuFileSpreadsheet,
  LuPencil,
  LuPlus,
  LuSend,
} from "react-icons/lu";
import {
  useToggleVoucher,
  useGetVouchers,
  type Voucher,
  type VoucherStateName,
} from "@/shared/core-vouchers";
import type { Pagination } from "@/shared/core-configs";
import EditModalVoucher from "../components/EditModalVoucher";
import ConfirmDialogMailVoucher from "../components/ConfirmDialogMailVoucher";
import ModalProductsTable from "@/core/products/components/ModalProductsTable";
import { VoucherTableColumns } from "../Columns";
import { PageContainer } from "@/ui/containers/PageContainer";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { downloadPdfVoucher } from "../utils/downloadPdfVoucher";
import { downloadVouchersExcel } from "../utils/downloadVouchersExcel";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/shared/session";
import qr from "query-string";
import ConfirmDialogValidateVoucher from "../components/ConfirmDialogValidateVoucher";
import { trackEvent } from "@/utils/firebase/analytics";
import { toast } from "@/ui/chakra/toaster";
import { UserAccess } from "@/core/session/components/UserAccess";
import { ModalDownloadAuditLog } from "@/core/logbooks/components/ModalDownloadLogbooks";
import { TableFilters } from "@/ui/filters/TableFilters";
import {
  countActiveFilters,
  type FilterValues,
} from "@/ui/filters/filterValues";
import { toVoucherFilterParams } from "../utils/voucherFilters";
import { useVoucherFilterFields } from "../useCases/useVoucherFilterFields";
import {
  BulkVoucherDialog,
  type BulkVoucherMode,
} from "../components/BulkVoucherDialog";
import { downloadPdfVouchers } from "../utils/downloadPdfVoucher";

const MAX_SELECTED_VOUCHERS = 50;

type StateFilter = "all" | VoucherStateName;

const STATE_FILTERS: { value: StateFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "Pendiente", label: "Pendientes" },
  { value: "Validado", label: "Validados" },
];

export const VouchersPage = () => {
  const navigate = useNavigate();

  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });

  const [showDisabled, setShowDisabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");
  const filterFields = useVoucherFilterFields();
  const [extraFilters, setExtraFilters] = useState<FilterValues>({});
  const extraFilterParams = useMemo(
    () => toVoucherFilterParams(extraFilters),
    [extraFilters],
  );
  const hasExtraFilters = countActiveFilters(extraFilters) > 0;

  const [selected, setSelected] = useState<Map<string, Voucher>>(new Map());
  const [bulkMode, setBulkMode] = useState<BulkVoucherMode | null>(null);
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const [isOpenAuditLog, setIsOpenAuditLog] = useState(false);

  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });

  const { session } = useAuth();

  const {
    data: vouchers,
    isPending,
    pagination: metaVoucher,
  } = useGetVouchers({
    page: pagination.page,
    pageSize: pagination.pageSize,
    showDisabled: showDisabled,
    searchTerm: debouncedSearchTerm,
    ...order,
    zoneId: session?.zone ?? "",
    state: stateFilter === "all" ? undefined : stateFilter,
    ...extraFilterParams,
  });

  const { toggleVoucher } = useToggleVoucher();
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenSendVoucher, setIsOpenSendVoucher] = useState(false);
  const [isOpenValidateVoucher, setIsOpenValidateVoucher] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [productsVoucher, setProductsVoucher] = useState<Voucher | null>(null);

  const handleDownloadClick = async (item: Voucher) => {
    const fileName = `voucher-${item.code}`;
    await notify.run(() => downloadPdfVoucher(item.id, fileName), {
      loading: `Generando el PDF del vale ${item.code}…`,
      error: "No se pudo descargar el PDF",
    });
  };
  const handleCopyVoucher = async (item: Voucher) => {
    item.products = [];
    navigate("/create-voucher", {
      state: { voucher: item },
    });
  };

  const handleOnProductsClick = (voucher: Voucher) => {
    setProductsVoucher(voucher);
  };

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = useCallback(
    async (voucher: Voucher) => {
      const ok = await confirmToggle(
        Boolean(voucher.enabled),
        `el vale ${voucher.code}`,
      );
      if (!ok) return;
      await toggleVoucher(
        {
          id: voucher.id,
          enabled: !voucher.enabled,
        },
        {
          onSuccess: () =>
            notify.success(
              voucher.enabled ? "Vale deshabilitado" : "Vale habilitado",
            ),
          onError: (error) =>
            notify.error(
              "No se pudo actualizar el vale",
              error,
              "Stock insuficiente para volver a habilitarlo.",
            ),
        },
      );
    },
    [toggleVoucher, confirmToggle],
  );

  const voucherTableColumns = useMemo(
    () => VoucherTableColumns(handleOnProductsClick, handleToggleEnabled),
    [handleToggleEnabled],
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSelected(new Map());
  }, [debouncedSearchTerm, stateFilter, extraFilterParams, showDisabled]);

  const handleSelectionChange = (items: Voucher[], isSelected: boolean) => {
    const next = new Map(selected);
    for (const item of items) {
      if (!isSelected) {
        next.delete(item.id);
      } else if (next.size < MAX_SELECTED_VOUCHERS) {
        next.set(item.id, item);
      }
    }
    if (isSelected && items.some((item) => !next.has(item.id))) {
      notify.info(
        `Puedes seleccionar hasta ${MAX_SELECTED_VOUCHERS} vales a la vez`,
      );
    }
    setSelected(next);
  };

  const selectedVouchers = useMemo(() => [...selected.values()], [selected]);

  const handleBulkDownload = async () => {
    setDownloadingBulk(true);
    try {
      await notify.run(
        () => downloadPdfVouchers(selectedVouchers.map((v) => v.id)),
        {
          loading: `Generando el PDF de ${selectedVouchers.length} vales…`,
          error: "No se pudo descargar el PDF",
        },
      );
    } finally {
      setDownloadingBulk(false);
    }
  };

  const clearAllFilters = () => {
    setStateFilter("all");
    setExtraFilters({});
  };

  const handleOnSortChange = (sort: string, order: string) => {
    setOrder({ sort, order });
  };

  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    const params = qr.stringify({
      zoneId: session?.zone,
      showDisabled,
      searchTerm: debouncedSearchTerm,
      sort: order.sort,
      order: order.order,
      state: stateFilter === "all" ? undefined : stateFilter,
      ...extraFilterParams,
    });
    setExporting(true);
    try {
      await notify.run(() => downloadVouchersExcel(params), {
        loading: "Preparando el Excel de vales…",
        error: "No se pudo exportar a Excel",
      });
    } finally {
      setExporting(false);
    }
  };

  const stateCounts = useMemo<Record<StateFilter, number> | undefined>(() => {
    const countByState = metaVoucher?.countByState;
    if (!countByState) return undefined;
    const Pendiente = countByState.Pendiente ?? 0;
    const Validado = countByState.Validado ?? 0;
    return { all: Pendiente + Validado, Pendiente, Validado };
  }, [metaVoucher?.countByState]);

  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Vales de productos"
      actions={
        <UserAccess roles={["admin", "supervisor"]}>
          <Button variant="outline" onClick={() => setIsOpenAuditLog(true)}>
            <LuDownload />
            Bitácora
          </Button>
          <Button colorPalette="brand" asChild>
            <RouterLink to="/create-voucher">
              <LuPlus />
              Crear vale
            </RouterLink>
          </Button>
        </UserAccess>
      }
    >
      <Table
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        isLoading={isPending}
        page={pagination.page}
        hasActiveFilters={stateFilter !== "all" || hasExtraFilters}
        onClearFilters={clearAllFilters}
        selectedKeys={[...selected.keys()]}
        onSelectionChange={handleSelectionChange}
        onClearSelection={() => setSelected(new Map())}
        bulkActions={
          <>
            <Button
              size="sm"
              variant="outline"
              colorPalette="brand"
              bg="bg.panel"
              onClick={() => setBulkMode("validate")}
            >
              <LuCheck />
              Validar
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorPalette="brand"
              bg="bg.panel"
              onClick={handleBulkDownload}
              loading={downloadingBulk}
            >
              <LuDownload />
              Descargar PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorPalette="brand"
              bg="bg.panel"
              onClick={() => setBulkMode("send")}
            >
              <LuSend />
              Enviar por correo
            </Button>
          </>
        }
        emptyDescription="Los vales registran la entrega de productos a un solicitante."
        emptyAction={
          <UserAccess roles={["admin", "supervisor"]}>
            <Button colorPalette="brand" size="sm" asChild>
              <RouterLink to="/create-voucher">
                <LuPlus />
                Crear vale
              </RouterLink>
            </Button>
          </UserAccess>
        }
        data={vouchers}
        headers={voucherTableColumns}
        extractItemKey={(item) => item.id}
        pages={metaVoucher?.totalPages}
        totalItems={metaVoucher?.count}
        itemsLabel="vales"
        searchPlaceholder="Buscar por código, tipo, empresa..."
        showPagination
        showDisabledCheck
        onShowDisabledChange={setShowDisabled}
        onSortChange={handleOnSortChange}
        onSearch={setSearchTerm}
        onChangePage={(page) =>
          setPagination({ ...pagination, page: page + 1 })
        }
        hiddenColumnsKey="vouchers"
        filters={
          <HStack gap={1.5} flexWrap="wrap">
            <FilterChips
              options={STATE_FILTERS}
              value={stateFilter}
              onChange={setStateFilter}
              counts={stateCounts}
            />
            <TableFilters
              fields={filterFields}
              value={extraFilters}
              onChange={setExtraFilters}
            />
          </HStack>
        }
        headerActions={
          <Button
            size="sm"
            variant="outline"
            colorPalette="brand"
            onClick={handleExportExcel}
            loading={exporting}
          >
            <LuFileSpreadsheet />
            Exportar a Excel
          </Button>
        }
        actions={[
          {
            label: "Enviar",
            onClick: (item) => {
              setSelectedVoucher(item as Voucher);
              trackEvent("voucher_action", {
                action: "send_email",
                voucher_id: (item as Voucher).id,
              });
              setIsOpenSendVoucher(true);
            },
            icon: <LuSend size={18} />,
          },
          {
            label: "Validar",
            onClick: (item) => {
              const voucher = item as Voucher;
              if (voucher.voucherState.name === "Validado") {
                toast({
                  title: "Vale ya validado",
                  description: "Este vale ya ha sido validado anteriormente",
                  status: "info",
                  duration: 5000,
                  isClosable: true,
                });
                return;
              }
              setSelectedVoucher(item as Voucher);
              trackEvent("voucher_action", {
                action: "validate",
                voucher_id: voucher.id,
              });
              setIsOpenValidateVoucher(true);
            },
            icon: <LuCheck size={18} />,
          },
          {
            label: "Descargar",
            onClick: (item) => {
              handleDownloadClick(item as Voucher);
            },
            icon: <LuDownload size={18} />,
          },
          {
            label: "Crear a partir de este",
            onClick: (item) => {
              handleCopyVoucher(item as Voucher);
            },
            icon: <LuCopy size={18} />,
          },
          {
            label: "Editar",
            onClick: (item) => {
              setIsOpenEditModal(true);
              setSelectedVoucher(item as Voucher);
            },
            icon: <LuPencil size={18} />,
          },
        ]}
      />
      {productsVoucher && (
        <ModalProductsTable
          products={productsVoucher.products}
          title={`Productos del vale ${productsVoucher.code}`}
          onClose={() => setProductsVoucher(null)}
          isOpen={!!productsVoucher}
        />
      )}
      {selectedVoucher && (
        <EditModalVoucher
          isOpen={isOpenEditModal}
          onClose={() => setIsOpenEditModal(false)}
          selectedVoucher={selectedVoucher}
        />
      )}
      {selectedVoucher && (
        <ConfirmDialogMailVoucher
          isOpen={isOpenSendVoucher}
          onClose={() => setIsOpenSendVoucher(false)}
          voucherId={selectedVoucher.id}
        />
      )}
      {selectedVoucher && (
        <ConfirmDialogValidateVoucher
          isOpen={isOpenValidateVoucher}
          onClose={() => setIsOpenValidateVoucher(false)}
          voucherId={selectedVoucher.id}
        />
      )}
      <BulkVoucherDialog
        mode={bulkMode}
        vouchers={selectedVouchers}
        onClose={() => setBulkMode(null)}
        onDone={() => setSelected(new Map())}
      />
      <ModalDownloadAuditLog
        isOpen={isOpenAuditLog}
        onClose={() => setIsOpenAuditLog(false)}
      />
    </PageContainer>
  );
};
